import SideImageSection from "features/authentication/components/SideImageSection";
import PinRecoveryModal from "components/settings/pin-recovery-modal/PinRecoveryModal";
import Button from "components/shared/button/Button";
import ErrorMessage from "components/shared/error-message/ErrorMessage";
import InputField from "components/shared/input-field/InputField";
import Loader from "components/shared/loader/Loader";
import Pin from "components/shared/pin/Pin";
import { serverUrlInputFieldTooltip } from "config/constants/tooltips";
import { defaultServerUrl } from "config/constants/urls";
import GuiContext from "config/context/guiContext";
import TableContext from "config/context/tableContext";
import useCheckIDBG from "features/authentication/hooks/useCheckIDBG";
import useLogin from "features/authentication/hooks/useLogin";
import { useValidateServer } from "features/authentication/hooks/useRegisterHook";
import useTheme from "config/useTheme";
import { isUndefined } from "lodash";
import { useContext, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import styles from "./Login.module.css";
import { initPinState } from "components/shared/pin/init-state";

const Login = () => {
  const { setInitTabStructAndComplex, setActiveComposition } =
    useContext(TableContext);
  const { userData, changeUserData, URL } = useContext(GuiContext);
  const { dark } = useTheme();
  const [pin, setPin] = useState(initPinState);
  const [isLoading, setIsLoading] = useState(false);
  const [idbg, setIdbg] = useState();
  const [serverURL, setServerURL] = useState(defaultServerUrl);
  const [isRecoveryModalOpen, setIsRecoveryModalOpen] = useState(false);
  const { validateServer, isServerValid, resetState, validateServerErrorMSG } =
    useValidateServer();
  const navigate = useNavigate();
  const logo = dark ? "img/logo-dark.png" : "img/logo.png";

  const { signIn, loginError } = useLogin(
    URL,
    navigate,
    setInitTabStructAndComplex,
    setActiveComposition,
    changeUserData,
  );
  const { idbgExists, checkDuplicate } = useCheckIDBG(URL);

  const handleSignIn = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    await signIn(idbg, pin, serverURL);
    setIsLoading(false);
  };

  useEffect(() => {
    if (userData.accounts) {
      navigate("/dashboard");
    }
  });

  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      validateServer(serverURL);
    }, 500);

    return () => clearTimeout(debounceTimer);
  }, [serverURL, resetState, validateServer]);

  return (
    <main className={`${styles.loginPage}`}>
      <section className={`${styles.loginSection}`}>
        <form onSubmit={handleSignIn}>
          <div className="gap-1 column">
            <div>
              <img className={styles.logo} src={logo} alt="logo" />
              <div className={`gap-1 ${styles.welcome} column`}>
                <h2>Welcome back!</h2>
                <p>Let's get started. Please enter your login details.</p>
              </div>
            </div>
            <div>
              <label htmlFor="account-name">
                <span>*</span>
                Account name
              </label>
              <InputField
                required
                id="accountName"
                name="account-name"
                type="text"
                placeholder="Enter your account name"
                value={idbg || ""}
                onChange={(e) => setIdbg(e.target.value)}
                onBlur={() => checkDuplicate(idbg)}
                className={styles.inputField}
                isValid={idbgExists}
              />
              <ErrorMessage
                value={
                  !isUndefined(idbgExists) && !idbgExists
                    ? "Account name does not exist"
                    : null
                }
                className={styles.errorMSG}
              />
            </div>
            <div>
              <label htmlFor="server-url">
                <span>*</span>
                Inery Node URL
              </label>
              <div data-tooltip={serverUrlInputFieldTooltip}>
                <InputField
                  id="serverURL"
                  name="server-url"
                  required
                  value={serverURL}
                  onChange={(e) => setServerURL(e.target.value)}
                  type="text"
                  placeholder="Enter your node URL"
                  className={styles.inputField}
                  isValid={isServerValid}
                />
              </div>
              <ErrorMessage
                value={loginError.serverURL}
                className={styles.errorMSG}
              />
              <ErrorMessage
                value={validateServerErrorMSG}
                className={styles.errorMSG}
              />
            </div>
            <div>
              <label htmlFor="pin">
                <span>*</span>
                PIN code
              </label>
              <Pin
                value={pin}
                onChange={setPin}
                isValid={!isUndefined(loginError.pin)}
              />
              <ErrorMessage value={loginError.pin} />
            </div>
            <div className="row justify-center m-t-1 m-b-1">
              <Button
                buttonType="text"
                variant="secondary"
                onClick={() => setIsRecoveryModalOpen(true)}
              >
                Change PIN code
              </Button>
            </div>
            <div className={`column gap-05`}>
              <ErrorMessage value={loginError.form} />
              {isLoading ? (
                <Loader />
              ) : (
                <Button
                  type="submit"
                  buttonType="contained"
                  variant="primary"
                  expand="full"
                  disabled={
                    !isServerValid ||
                    !Object.values(pin).every((val) => !isNaN(parseInt(val)))
                  }
                >
                  Log In
                </Button>
              )}
              <div className="row justify-center">
                <p>Don't have an account?&nbsp;</p>
                <Link to="/register">Register</Link>
              </div>
            </div>
          </div>
        </form>
      </section>
      <SideImageSection className={styles.sideImageSection} />
      <PinRecoveryModal
        isRecoveryModalOpen={isRecoveryModalOpen}
        setIsRecoveryModalOpen={setIsRecoveryModalOpen}
      />
    </main>
  );
};

export default Login;
